import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';
import { inferParentRelationshipType } from '../../constants/relationships';
import { apiRequest } from '../../services/apiClient';
import {
  contractTypesToOptions,
  getContractFacets,
  getContractTypes,
  matchCounterpartyName,
  searchContracts,
} from './contractLookupApi';

export function createEmptyContractFormData() {
  return {
    title: '',
    shortName: '',
    lightsparkEntity: '',
    contractType: 'Other',
    contractTypeOther: '',
    counterparty: '',
    counterpartyGroup: '',
    additionalCounterparties: [],
    counterpartyAliases: [],
    counterpartyContactName: '',
    counterpartyContactEmail: '',
    counterpartyContactPhone: '',
    counterpartyContactRole: '',
    status: 'draft',
    draftSource: '',
    effectiveDate: '',
    executedDate: '',
    expirationDate: '',
    renewalType: 'fixed_term',
    renewalNoticeDays: 30,
    terminationNoticeDays: 30,
    contractValue: '',
    paymentTerms: 'none',
    internalOwner: '',
    confidentialityStructure: '',
    confidentialityDisclosingParty: '',
    summary: '',
    keyRisks: [],
    keyCommitments: [],
    financialTerms: [],
    openIssues: [],
    tags: [],
    notes: '',
    restricted: false,
    category: '',
    parentContractId: null,
    parentRelationshipType: '',
  };
}

export function createContractFormDataFromContract(contract = {}) {
  return {
    ...createEmptyContractFormData(),
    title: contract.title || '',
    shortName: contract.shortName || '',
    lightsparkEntity: contract.lightsparkEntity || '',
    contractType: contract.contractType || 'Other',
    contractTypeOther: contract.contractTypeOther || '',
    counterparty: contract.counterparty || '',
    counterpartyGroup: contract.counterpartyGroup || '',
    additionalCounterparties: Array.isArray(contract.additionalCounterparties)
      ? contract.additionalCounterparties
      : [],
    counterpartyAliases: Array.isArray(contract.counterpartyAliases)
      ? contract.counterpartyAliases
      : [],
    counterpartyContactName: contract.counterpartyContactName || '',
    counterpartyContactEmail: contract.counterpartyContactEmail || '',
    counterpartyContactPhone: contract.counterpartyContactPhone || '',
    counterpartyContactRole: contract.counterpartyContactRole || '',
    status: contract.status || 'draft',
    draftSource: contract.draftSource || '',
    effectiveDate: contract.effectiveDate || '',
    executedDate: contract.executedDate || '',
    expirationDate: contract.expirationDate || '',
    renewalType: contract.renewalType || 'fixed_term',
    renewalNoticeDays: contract.renewalNoticeDays ?? 30,
    terminationNoticeDays: contract.terminationNoticeDays ?? 30,
    contractValue: contract.contractValue || '',
    paymentTerms: contract.paymentTerms || 'none',
    internalOwner: contract.internalOwner || '',
    confidentialityStructure: contract.confidentialityStructure || '',
    confidentialityDisclosingParty: contract.confidentialityDisclosingParty || '',
    summary: contract.summary || '',
    keyRisks: Array.isArray(contract.keyRisks) ? contract.keyRisks : [],
    keyCommitments: Array.isArray(contract.keyCommitments) ? contract.keyCommitments : [],
    financialTerms: Array.isArray(contract.financialTerms) ? contract.financialTerms : [],
    openIssues: Array.isArray(contract.openIssues) ? contract.openIssues : [],
    tags: contract.tags || [],
    notes: contract.notes || '',
    restricted: !!contract.restricted,
    category: contract.category || '',
    parentContractId: contract.parentContractId || null,
    parentRelationshipType: contract.parentRelationshipType || '',
  };
}

export function validateContractFormField(name, value) {
  switch (name) {
    case 'title':
      if (!String(value || '').trim()) return 'Title is required';
      if (String(value).length < 3) return 'Title must be at least 3 characters';
      break;
    default:
      break;
  }
  return null;
}

export function validateContractFormData(formData) {
  return Object.keys(formData).reduce((acc, key) => {
    const error = validateContractFormField(key, formData[key]);
    return error ? { ...acc, [key]: error } : acc;
  }, {});
}

export function buildContractFormPayload(formData) {
  return {
    ...formData,
    contractValue: formData.contractValue === '' ? null : Number(formData.contractValue),
    executedDate: formData.executedDate || null,
    expirationDate: formData.expirationDate || null,
  };
}

function uniqueCaseInsensitiveAppend(items, value) {
  const clean = String(value || '').trim();
  if (!clean) return items || [];
  const list = items || [];
  return list.some((existing) => String(existing).toLowerCase() === clean.toLowerCase())
    ? list
    : [...list, clean];
}

export default function useContractFormWorkflow({ id, isEditing, showToast }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(createEmptyContractFormData);
  const [parentContract, setParentContract] = useState(null);
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [counterpartySuggestions, setCounterpartySuggestions] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [contractTypeOptions, setContractTypeOptions] = useState([
    { value: 'Other', label: 'Other' },
  ]);
  const [cpFuzzySuggestions, setCpFuzzySuggestions] = useState([]);
  const [cpFuzzyDismissed, setCpFuzzyDismissed] = useState(false);
  const [existingShortNamesForCp, setExistingShortNamesForCp] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  // Snapshot of the last saved/loaded form state, for the unsaved-changes
  // guard. Serialized once here rather than deep-compared field by field.
  const [baseline, setBaseline] = useState(() =>
    JSON.stringify(createEmptyContractFormData())
  );

  const isDirty = !loading && JSON.stringify(formData) !== baseline;
  const bypassUnsavedGuard = useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    if (!isEditing) return;

    apiRequest(`/contracts/${id}`)
      .then((data) => {
        const loaded = createContractFormDataFromContract(data);
        setFormData(loaded);
        setBaseline(JSON.stringify(loaded));
        setParentContract(data.parent || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id, isEditing]);

  useEffect(() => {
    getContractTypes()
      .then((data) => {
        if (!data || !Array.isArray(data.contractTypes)) return;
        setContractTypeOptions(contractTypesToOptions(data.contractTypes));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getContractFacets()
      .then((data) => {
        if (!data) return;
        if (Array.isArray(data.counterpartyGroups)) setGroupSuggestions(data.counterpartyGroups);
        if (Array.isArray(data.counterparties)) setCounterpartySuggestions(data.counterparties);
        if (Array.isArray(data.tags)) setTagSuggestions(data.tags);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const counterparty = (formData.counterparty || '').trim();
    if (!counterparty) {
      setExistingShortNamesForCp([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await searchContracts({ counterparty, limit: 50, excludeId: id });
        if (!cancelled) setExistingShortNamesForCp(data?.contracts || []);
      } catch (_) {
        if (!cancelled) setExistingShortNamesForCp([]);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.counterparty, id]);

  useEffect(() => {
    if (cpFuzzyDismissed) {
      setCpFuzzySuggestions([]);
      return undefined;
    }

    const name = (formData.counterparty || '').trim();
    if (!name) {
      setCpFuzzySuggestions([]);
      return undefined;
    }

    const handle = setTimeout(() => {
      matchCounterpartyName(name)
        .then((data) => setCpFuzzySuggestions(data?.suggestions || []))
        .catch(() => setCpFuzzySuggestions([]));
    }, 350);

    return () => clearTimeout(handle);
  }, [formData.counterparty, cpFuzzyDismissed]);

  const handleChange = (event) => {
    const { name, value, type } = event.target;
    const newValue = type === 'number' ? (value === '' ? '' : Number(value)) : value;

    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: newValue,
      };
      if (name === 'contractType' && prev.parentContractId && !prev.parentRelationshipType) {
        next.parentRelationshipType = inferParentRelationshipType({
          contractType: newValue,
          parentReferenceText: prev.parentReferenceText,
          title: prev.title,
          shortName: prev.shortName,
        });
      }
      return next;
    });

    if (touched[name]) {
      const fieldError = validateContractFormField(name, newValue);
      setErrors((prev) => ({ ...prev, [name]: fieldError }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldError = validateContractFormField(name, value);
    setErrors((prev) => ({ ...prev, [name]: fieldError }));
  };

  const handleAddTag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: uniqueCaseInsensitiveAppend(prev.tags, tag),
    }));
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: (prev.tags || []).filter((tag) => tag !== tagToRemove),
    }));
  };

  const makeAddTo = (field) => (value) => {
    setFormData((prev) => ({ ...prev, [field]: [...(prev[field] || []), value] }));
  };

  const makeRemoveFrom = (field) => (value) => {
    setFormData((prev) => ({ ...prev, [field]: (prev[field] || []).filter((item) => item !== value) }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const newErrors = validateContractFormData(formData);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      showToast('Please fix the errors before submitting', 'error');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const contractData = buildContractFormPayload(formData);
      let contractId = id;

      if (isEditing) {
        await apiRequest(`/contracts/${id}`, {
          method: 'PUT',
          json: contractData,
        });
        showToast('Contract updated successfully', 'success');
      } else {
        const newContract = await apiRequest('/contracts', {
          method: 'POST',
          json: contractData,
        });
        contractId = newContract.id;
        showToast('Contract created successfully', 'success');
      }

      bypassUnsavedGuard();
      navigate(`/contracts/${contractId}`);
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
      setSaving(false);
    }
  };

  return {
    contractTypeOptions,
    counterpartySuggestions,
    cpFuzzySuggestions,
    error,
    errors,
    existingShortNamesForCp,
    formData,
    groupSuggestions,
    handleAddTag,
    handleBlur,
    handleChange,
    handleRemoveTag,
    handleSubmit,
    isDirty,
    loading,
    makeAddTo,
    makeRemoveFrom,
    parentContract,
    saving,
    setCpFuzzyDismissed,
    setCpFuzzySuggestions,
    setFormData,
    setParentContract,
    tagSuggestions,
    touched,
  };
}
